import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import AddEditButton from "../../reusable-components/AddEditButton/AddEditButton";
import {
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import DialogTitle from "../DialogTitle/DialogTitle";
import { Props, State } from "./interfaces";
import { useTranslation } from "react-i18next";
import { MailInboxAddresseeDTO } from "../../models/mail-inbox-addressee/mail-inbox-addressee-dto";
import DialogActions from "../DialogActions/DialogActions";
import { MailInboxAddresseesApiService } from "../../services/mail-inbox-addressees/mail-inbox-addressees-api.service";
import AutocompleteDropdown from "../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { Block, Container, RecipientsContainer, SingleRecipient } from "./styles";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import AddMailInboxAddresseeRecipient from "./AddMailInboxAddresseeRecipient";
import { MailInboxAddreseeRecipientRef } from "src/models/mail-inbox-addressee-recipients";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import { CountriesApiService } from "src/services/countries/countries-api.service";
import { EMAIL_PATTERN, isValidString, PHONE_NUMBER_PATTERN } from "src/utils/validation/isValidString";
const AddEditMailInboxAddressee = ({
  item,
  effect,
  handleNotifyParent,
  buttonText,
  mailInbox,
  buttonVariant,
  customButtonStyle,
}: Props) => {
  const tRP = "components.add_edit_mail_inbox_addressee";
  const { t } = useTranslation();

  const theme = useTheme();

  const DEFAULT_ITEM: Partial<MailInboxAddresseeDTO> = useMemo(
    () => ({
      id: "",
      isDeleted: false,
      mailInbox: mailInbox ?? null,
      nickname: "",
      isActive: true,
      recipients: [],
      contactPersonName: "",
      email: "",
      phoneNumber: "",
      companyName: "",
      addressLineOne: "",
      addressLineTwo: "",
      zip: "",
      location: "",
      country: null,
    }),
    [mailInbox]
  );

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      loading: false,
      error: "",
      CCDOpen: false,
      item: item ?? ({ ...DEFAULT_ITEM, id: uuid() } as MailInboxAddresseeDTO),
      requiredFieldsValidated: effect === "add" ? true : false,
    }),
    [item, DEFAULT_ITEM, effect]
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  //#region handle change
  const handleChange = useCallback(
    (field: keyof MailInboxAddresseeDTO, value: MailInboxAddresseeDTO[keyof MailInboxAddresseeDTO]) => {
      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          [field]: value,
        },
      }));
    },
    []
  );
  //#endregion

  //#region handle save
  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new MailInboxAddresseesApiService().saveItems([
        {
          ...state.item,
          phoneNumber: state.item.phoneNumber.trim(),
        },
      ]);
      if (response.error) {
        setState((state) => ({ ...state, error: response.error }));
      } else {
        setState({ ...INITIAL_STATE });
        handleNotifyParent(state.item);
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [INITIAL_STATE, state.item, handleNotifyParent]);
  //#endregion

  //#region remove recipient
  const handleRemoveRecipient = useCallback((recipientId: string) => {
    setState((state) => ({
      ...state,
      item: {
        ...state.item,
        recipients: [...state.item.recipients.filter((recipient) => recipient.id !== recipientId)],
      },
    }));
  }, []);
  //#endregion

  //#region fields validated
  const areFieldsValidated = useCallback(() => {
    const areValidated =
      !!state.item.addressLineOne &&
      !!state.item.zip &&
      !!state.item.location &&
      !!state.item.mailInbox &&
      !!state.item.nickname &&
      state.item.nickname.length < 21 &&
      (!state.item.email || (!!state.item.email && isValidString(EMAIL_PATTERN, state.item.email)));

    setState((state) => ({
      ...state,
      requiredFieldsValidated: areValidated,
    }));
  }, [state.item]);

  useEffect(() => {
    areFieldsValidated();
  }, [state.item]);
  //#endregion

  useEffect(() => {
    setState((state) => ({
      ...state,
      item: {
        ...state.item,
        mailInbox: mailInbox,
      },
    }));
  }, [mailInbox]);

  return (
    <>
      <AddEditButton
        title={effect === "edit" ? `${t(`${tRP}.title_edit`)}` : `${t(`${tRP}.title_add`)}`}
        effect={effect}
        onClick={() => {
          setState((state) => ({ ...state, open: true }));
        }}
        {...(buttonVariant === "text" && { buttonText: buttonText ?? "Edit" })}
        customStyle={{ ...customButtonStyle }}
      />
      <Dialog
        open={state.open}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          title={effect === "edit" ? `${t(`${tRP}.title_edit`)}` : `${t(`${tRP}.title_add`)}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <Container>
              <Block>
                {/* <AutocompleteDropdown
                  selectedValue={state.item.mailInbox}
                  serviceApi={new MailInboxApiService()}
                  field={"mailInbox"}
                  handleChange={handleChange}
                  disabled={!!mailInbox}
                  labelKey={`${t(`${tRP}.fields.mail_inbox`)}`}
                /> */}
                <TextField
                  value={state.item.nickname}
                  label={`${t(`${tRP}.fields.nickname`)}`}
                  onChange={({ currentTarget }) => handleChange("nickname", currentTarget.value)}
                  error={state.item.nickname.length > 100}
                  helperText={state.item.nickname.length > 100 ? `${t(`${tRP}.invalid_nickname`)}` : ""}
                  required
                />
                <TextField
                  value={state.item.contactPersonName}
                  label={`${t(`${tRP}.fields.contact_person_name`)}`}
                  onChange={({ currentTarget }) => handleChange("contactPersonName", currentTarget.value)}
                />
                <TextField
                  value={state.item.email}
                  label={`${t(`${tRP}.fields.email`)}`}
                  onChange={({ currentTarget }) => handleChange("email", currentTarget.value)}
                  error={!!state.item.email && !isValidString(EMAIL_PATTERN, state.item.email)}
                  helperText={
                    !!state.item.email &&
                    !isValidString(EMAIL_PATTERN, state.item.email) &&
                    `${t(`${tRP}.invalid_mail`)}`
                  }
                />
                <TextField
                  value={state.item.phoneNumber}
                  label={`${t(`${tRP}.fields.phone_number`)}`}
                  onChange={({ currentTarget }) => handleChange("phoneNumber", currentTarget.value)}
                  error={!!state.item.phoneNumber && !isValidString(PHONE_NUMBER_PATTERN, state.item.phoneNumber)}
                  helperText={
                    !!state.item.phoneNumber &&
                    !isValidString(PHONE_NUMBER_PATTERN, state.item.phoneNumber) &&
                    `${t(`${tRP}.invalid_phone_number`)}`
                  }
                />
                <TextField
                  value={state.item.companyName}
                  label={`${t(`${tRP}.fields.company_name`)}`}
                  onChange={({ currentTarget }) => handleChange("companyName", currentTarget.value)}
                />
                <TextField
                  value={state.item.addressLineOne}
                  label={`${t(`${tRP}.fields.address_line_one`)}`}
                  onChange={({ currentTarget }) => handleChange("addressLineOne", currentTarget.value)}
                  required
                />
                <TextField
                  value={state.item.addressLineTwo}
                  label={`${t(`${tRP}.fields.address_line_two`)}`}
                  onChange={({ currentTarget }) => handleChange("addressLineTwo", currentTarget.value)}
                />
              </Block>
              <Block>
                <TextField
                  value={state.item.zip}
                  label={`${t(`${tRP}.fields.zip`)}`}
                  onChange={({ currentTarget }) => handleChange("zip", currentTarget.value)}
                  required
                />
                <TextField
                  value={state.item.location}
                  label={`${t(`${tRP}.fields.location`)}`}
                  onChange={({ currentTarget }) => handleChange("location", currentTarget.value)}
                  required
                />
                <AutocompleteDropdown
                  serviceApi={new CountriesApiService()}
                  selectedValue={state.item.country}
                  field={"country"}
                  handleChange={handleChange}
                  labelKey={`${t(`${tRP}.fields.country`)}`}
                />
                <FormControlLabel
                  label={`${t(`${tRP}.fields.is_active`)}`}
                  control={
                    <Checkbox
                      checked={state.item.isActive}
                      onChange={(_, checked) => handleChange("isActive", checked)}
                    />
                  }
                />
                <RecipientsContainer>
                  <Grid
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                      marginBottom: "20px",
                    }}
                  >
                    <Typography>{`${t(`${tRP}.fields.recipient_list`)}`} : </Typography>
                    <AddMailInboxAddresseeRecipient
                      handleAddRecipient={(recipient: MailInboxAddreseeRecipientRef) =>
                        handleChange("recipients", [...state.item.recipients, recipient])
                      }
                      mailInboxAddressee={state.item}
                    />
                  </Grid>

                  {state.item.recipients.map((recipient, idx) => (
                    <SingleRecipient key={idx}>
                      <Typography>
                        &#8226;
                        {`${recipient.user?.email} (${
                          recipient.user?.name ?? `${recipient.user?.firstName} ${recipient.user?.lastName}`
                        })`}
                      </Typography>
                      <IconButton onClick={() => handleRemoveRecipient(recipient.id)}>
                        <FontAwesomeIcon
                          icon={faTrash}
                          color={theme.palette.error.main}
                        />
                      </IconButton>
                    </SingleRecipient>
                  ))}
                </RecipientsContainer>
              </Block>
            </Container>
          </LoadingOverlay>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={handleSave}
          disabledSaveButton={!state.requiredFieldsValidated}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => setState({ ...INITIAL_STATE })}
      />
    </>
  );
};

export default AddEditMailInboxAddressee;
